import { useCallback } from 'react';
import { Banner, Stack } from '@optimus-web/core';
import { WithScope } from '@mews-commander/types';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { canCurrentProfileOverrideRatePrice } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useInvalidateReservationGroupQuery } from '@mews-commander/core/entities';
import {
    getCheckedReservationIds,
    getCheckedReservationsAvailabilityBlockIds,
    getFirstCheckedReservationServiceId,
} from '../../selectors';
import { selectionHasCanceled } from '../../selectors/reservationModuleState/reservationSelection';
import { getHasCommonReservationsDefaultProductOrderItems } from '../../ducks/orderItems';
import { RESERVATION_ORDER_ITEMS_EXTENT } from '../../constants';
import { fetchCurrentEnterpriseCheckedReservations, fetchReservationModuleReduxEntities } from '../../actions';
import { DefaultTabLoader } from '../DefaultTabLoader';
import { RateForm } from './RateForm/RateForm';
import { AverageNightRateFormContainer } from './AverageNightRateForm/AverageNightRateForm';
import { RateExcludingProductsContainer } from './RateExcludingProducts/RateExcludingProducts';
import { RelativeAdjustmentRateFormContainer } from './RelativeAdjustmentRateForm/RelativeAdjustmentRateForm';

export const PricingTab: React.FC<WithScope> = ({ scope }) => (
    <DefaultTabLoader scope={scope}>
        <PricingTabComponent scope={scope} />
    </DefaultTabLoader>
);

const PricingTabComponent: React.FC<WithScope> = ({ scope }) => {
    const scopeProps = { scope };
    const serviceId = useCommanderSelector((s) => getFirstCheckedReservationServiceId(s, scopeProps));
    const checkedReservationIds = useCommanderSelector((s) => getCheckedReservationIds(s, scopeProps));
    const hasCanceledReservations = useCommanderSelector((s) => selectionHasCanceled(s, scopeProps));
    const availabilityBlockIds = useCommanderSelector((s) => getCheckedReservationsAvailabilityBlockIds(s, scopeProps));
    const showPerNightRateForm = useCommanderSelector((s) =>
        serviceId
            ? getHasCommonReservationsDefaultProductOrderItems(s, { serviceId, serviceOrderIds: checkedReservationIds })
            : false
    );
    const canOverrideRatePrice = useCommanderSelector(canCurrentProfileOverrideRatePrice);

    const dispatch = useCommanderThunkDispatch();
    const reservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);
    const invalidateReservationOrderItems = useInvalidateReservationGroupQuery(scope, {
        invalidateOrderItemsQuery: true,
    });
    const refetchGroup = useCallback(
        async () =>
            Promise.all([
                reservationModuleReactQueryEnabled
                    ? invalidateReservationOrderItems()
                    : dispatch(
                          fetchCurrentEnterpriseCheckedReservations({ scope, extent: RESERVATION_ORDER_ITEMS_EXTENT })
                      ),
                dispatch(fetchReservationModuleReduxEntities(scope, { balances: true })),
            ]),
        [dispatch, invalidateReservationOrderItems, reservationModuleReactQueryEnabled, scope]
    );

    const withRateForm = availabilityBlockIds.length === 0;
    return (
        <Stack spacing="200" vertical>
            {withRateForm && <RateForm scope={scope} reservationIds={checkedReservationIds} onSuccess={refetchGroup} />}
            {canOverrideRatePrice && !hasCanceledReservations && (
                <>
                    <AverageNightRateFormContainer
                        reservationIds={checkedReservationIds}
                        onSuccess={refetchGroup}
                        scope={scope}
                    />
                    <RelativeAdjustmentRateFormContainer
                        scope={scope}
                        reservationIds={checkedReservationIds}
                        onSuccess={refetchGroup}
                    />
                    {showPerNightRateForm && (
                        <RateExcludingProductsContainer
                            reservationIds={checkedReservationIds}
                            scope={scope}
                            onSuccess={refetchGroup}
                        />
                    )}
                </>
            )}
            {!canOverrideRatePrice && (
                <Banner
                    primaryText={<LocalizedText textKey={TextKey.RatePriceUpdateNotAllowedMessage} />}
                    variant="info"
                />
            )}
        </Stack>
    );
};
